import axios from 'axios'

const SET_USER_SETTINGS = 'SET_USER_SETTINGS'

/**
 * Vuex state for settings module
 * @type {Object}
 */
const state = {
    userSettings: [],
}

/**
 * Vuex mutations for settings module
 * @type {Object}
 */
const mutations = {
    [SET_USER_SETTINGS]: (state, payload) => {
        state.userSettings = payload
    },
}

/**
 * Vuex actions for settings module
 * @type {Object}
 */
const actions = {
    async fetchUserSettings({ commit }) {
        const { data } = await axios.post('/api/settings', {
            alias: 'user',
        })
        commit(SET_USER_SETTINGS, data.settings)
    },
}

/**
 * Helper function to get setting value by alias
 * 
 * @param {Array} settings - Array of user settings
 * @param {string} alias - Setting alias to find
 * @returns {boolean} Setting enabled status or false if not found
 */
const getSettingByAlias = (settings, alias) => {
    const setting = settings.find((s) => s.alias === alias)
    return setting ? setting.is_enabled : false
}

/**
 * Helper function to get a parsed setting value by alias
 * @param {Array} settings - Array of user settings
 * @param {string} alias - Setting alias to find
 * @param {*} [fallback=[]] - Value returned when the setting is missing
 * @returns {*} Parsed setting value (JSON-decoded if stored as a string), or fallback if not found
 */
const getSettingValueByAlias = (settings, alias, fallback = []) => {
    const setting = settings.find((s) => s.alias === alias)
    if (!setting) return fallback

    return typeof setting.value === 'string'
        ? JSON.parse(setting.value)
        : setting.value
}

/**
 * Vuex getters for settings module
 * @type {Object}
 */
const getters = {
    userSettings: (state) => state.userSettings,
    enabled_direct_messaging: (state) =>
        getSettingByAlias(state.userSettings, 'user_enable_direct_messaging'),
    allow_steel_tsc_selection: (state) =>
        getSettingByAlias(state.userSettings, 'user_allow_steel_tsc_selection'),
    // The phone number is stored as a raw string, so read the value directly
    // instead of JSON-decoding it like the list-based settings below
    bidpump_phone_number: (state) =>
        state.userSettings.find((s) => s.alias === 'user_bidpump_phone_number')?.value ?? null,
    default_steel_supply_time_options: (state) =>
        getSettingValueByAlias(state.userSettings, 'default_steel_supply_time_options'),
    default_pump_supply_time_options: (state) =>
        getSettingValueByAlias(state.userSettings, 'default_pump_supply_time_options'),
    default_transport_methods: (state) =>
        getSettingValueByAlias(state.userSettings, 'default_transport_methods'),
    default_truck_sizes: (state) =>
        getSettingValueByAlias(state.userSettings, 'default_truck_sizes'),
    default_logistics: (state) =>
        getSettingValueByAlias(state.userSettings, 'default_logistics'),
    default_site_restrictions: (state) =>
        getSettingValueByAlias(state.userSettings, 'default_site_restrictions'),
    default_pump_types: (state) =>
        getSettingValueByAlias(state.userSettings, 'default_pump_types'),
    default_pump_mixes: (state) =>
        getSettingValueByAlias(state.userSettings, 'default_pump_mixes'),
}

export default {
    namespaced: true,
    state,
    actions,
    mutations,
    getters,
}
